import { useState } from 'react';
import { useRessource } from '../../hooks/useRessource';
import { api } from '../../lib/apiClient';
import {
  Badge,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../../components/Elements';
import { formaterDateHeure, libelleAction } from '../../lib/format';
import type { EntreeAudit } from '../../types';

const ACTIONS = [
  'creation',
  'modification',
  'suppression',
  'consultation',
  'connexion',
  'deconnexion',
  'export',
  'elevation',
  'echec_connexion',
] as const;

function tonDe(action: string): 'neutre' | 'succes' | 'alerte' | 'danger' {
  if (action === 'suppression' || action === 'echec_connexion') return 'danger';
  if (action === 'creation') return 'succes';
  if (action === 'elevation' || action === 'export') return 'alerte';
  return 'neutre';
}

export function AuditPage() {
  const [action, setAction] = useState('');
  const [objetType, setObjetType] = useState('');
  const [detail, setDetail] = useState<EntreeAudit | null>(null);

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<EntreeAudit[]>('/api/admin/audit', { action, objetType, limite: 200 }),
    [action, objetType],
  );

  const colonnes: Colonne<EntreeAudit>[] = [
    {
      cle: 'date',
      entete: 'Horodatage',
      rendu: (e) => <span className="tabulaire whitespace-nowrap">{formaterDateHeure(e.date)}</span>,
    },
    {
      cle: 'auteur',
      entete: 'Auteur',
      rendu: (e) =>
        e.utilisateur_identifiant ? (
          <div>
            <div>
              {e.utilisateur_prenom} {e.utilisateur_nom}
            </div>
            <div className="font-mono text-xs text-encre-400">{e.utilisateur_identifiant}</div>
          </div>
        ) : (
          <span className="text-encre-400">—</span>
        ),
    },
    { cle: 'action', entete: 'Action', rendu: (e) => <Badge ton={tonDe(e.action)}>{libelleAction(e.action)}</Badge> },
    {
      cle: 'objet',
      entete: 'Objet',
      rendu: (e) => (
        <div>
          <div>{e.objet_type}</div>
          {e.objet_id && <div className="font-mono text-xs text-encre-400">{e.objet_id.slice(0, 8)}…</div>}
        </div>
      ),
    },
    { cle: 'entite', entete: 'Entite', rendu: (e) => e.entite_code ?? '—' },
    { cle: 'ip', entete: 'IP', rendu: (e) => <span className="tabulaire text-encre-500">{e.ip ?? '—'}</span> },
  ];

  return (
    <>
      <EnTetePage
        titre="Journal d’audit"
        description="Append only : aucune route ne permet de le modifier ni de le purger, quel que soit le role. Le compte applicatif n’en a d’ailleurs pas le droit en base."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="champ w-auto" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">Toutes les actions</option>
          {ACTIONS.map((valeur) => (
            <option key={valeur} value={valeur}>
              {libelleAction(valeur)}
            </option>
          ))}
        </select>

        <input
          className="champ w-auto"
          placeholder="Type d’objet (famille, utilisateur…)"
          value={objetType}
          onChange={(e) => setObjetType(e.target.value)}
        />

        {(action || objetType) && (
          <button
            type="button"
            className="bouton-secondaire"
            onClick={() => {
              setAction('');
              setObjetType('');
            }}
          >
            Effacer les filtres
          </button>
        )}
      </div>

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={donnees ?? []}
            cleDe={(e) => String(e.id)}
            messageVide="Aucune entree ne correspond."
            onLigneCliquee={setDetail}
          />
        </div>
      )}

      <Modale titre="Detail de l’entree" ouverte={Boolean(detail)} onFermer={() => setDetail(null)}>
        {detail && (
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-encre-400">Horodatage</dt>
                <dd className="tabulaire">{formaterDateHeure(detail.date)}</dd>
              </div>
              <div>
                <dt className="text-xs text-encre-400">Action</dt>
                <dd>{libelleAction(detail.action)}</dd>
              </div>
              <div>
                <dt className="text-xs text-encre-400">Objet</dt>
                <dd>{detail.objet_type}</dd>
              </div>
              <div>
                <dt className="text-xs text-encre-400">Identifiant</dt>
                <dd className="font-mono text-xs">{detail.objet_id ?? '—'}</dd>
              </div>
            </dl>

            {detail.contexte && (
              <div>
                <div className="text-xs text-encre-400">Contexte</div>
                <p>{detail.contexte}</p>
              </div>
            )}

            {/* RG-38 : le journal doit permettre de reconstituer qui a
                modifie quoi, quand, et depuis quelle valeur. */}
            {Boolean(detail.valeurs_avant ?? detail.valeurs_apres) && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs text-encre-400">Avant</div>
                  <pre className="max-h-64 overflow-auto rounded bg-encre-100 p-2 text-xs">
                    {JSON.stringify(detail.valeurs_avant, null, 2) ?? '—'}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 text-xs text-encre-400">Apres</div>
                  <pre className="max-h-64 overflow-auto rounded bg-encre-100 p-2 text-xs">
                    {JSON.stringify(detail.valeurs_apres, null, 2) ?? '—'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </Modale>
    </>
  );
}
